import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  MessageCircle, Send, ArrowLeft, Search, Filter, X, User, Clock,
  CheckCircle, AlertTriangle, ChevronDown, Tag, UserPlus, MoreVertical,
  FileText, Inbox, Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = "/api";

const STATUS_CONFIG = {
  open: { label: "Open", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  pending: { label: "Pending", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  resolved: { label: "Resolved", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  escalated: { label: "Escalated", color: "bg-red-500/20 text-red-400 border-red-500/30" },
};

const PRIORITY_CONFIG = {
  low: { label: "Low", color: "text-slate-400" },
  normal: { label: "Normal", color: "text-blue-400" },
  high: { label: "High", color: "text-amber-400" },
  urgent: { label: "Urgent", color: "text-red-400" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  return <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full border", cfg.color)}>{cfg.label}</span>;
}

function PriorityDot({ priority }) {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
  return <span className={cn("w-2 h-2 rounded-full inline-block", cfg.color.replace("text-", "bg-"))} title={cfg.label} />;
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function AdminChat() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedConv, setSelectedConv] = useState(null);
  const [newMsg, setNewMsg] = useState("");
  const [msgType, setMsgType] = useState("message"); // "message" or "internal_note"
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msgsLoading, setMsgsLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [assignedFilter, setAssignedFilter] = useState(searchParams.get("assigned") || "");
  const [showActions, setShowActions] = useState(false);

  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch conversations
  const fetchConversations = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter) params.set("status", statusFilter);
    if (assignedFilter) params.set("assigned_to", assignedFilter);
    axios.get(`${API}/admin/chat/conversations?${params}`)
      .then(res => {
        setConversations(res.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [search, statusFilter, assignedFilter]);

  useEffect(() => {
    fetchConversations();
    pollRef.current = setInterval(fetchConversations, 8000);
    return () => clearInterval(pollRef.current);
  }, [fetchConversations]);

  // Select conversation from URL param
  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === conversationId || c.user_id === conversationId);
      if (conv) {
        setSelectedConv(conv);
        setSelectedUserId(conv.user_id);
      }
    }
  }, [conversationId, conversations]);

  // Load messages when conversation selected
  useEffect(() => {
    if (!selectedUserId) return;
    setMsgsLoading(true);
    axios.get(`${API}/admin/chat/messages/${selectedUserId}`)
      .then(res => { setMessages(res.data || []); setMsgsLoading(false); })
      .catch(() => setMsgsLoading(false));

    const poll = setInterval(() => {
      axios.get(`${API}/admin/chat/messages/${selectedUserId}`).then(res => setMessages(res.data || []));
    }, 5000);
    return () => clearInterval(poll);
  }, [selectedUserId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const selectConversation = (conv) => {
    setSelectedConv(conv);
    setSelectedUserId(conv.user_id);
    navigate(`/admin/chat/${conv.id}`, { replace: true });
  };

  const handleBack = () => {
    setSelectedConv(null);
    setSelectedUserId(null);
    setMessages([]);
    navigate("/admin/chat", { replace: true });
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !selectedUserId || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/chat/messages/${selectedUserId}`, {
        message: newMsg.trim(),
        type: msgType,
      });
      setNewMsg("");
      setMsgType("message");
      const res = await axios.get(`${API}/admin/chat/messages/${selectedUserId}`);
      setMessages(res.data || []);
      fetchConversations();
    } catch {
      /* keep message for retry */
    }
    setSending(false);
  };

  const updateConversation = async (updates) => {
    if (!selectedConv) return;
    try {
      await axios.put(`${API}/admin/chat/conversations/${selectedConv.id}`, updates);
      fetchConversations();
      setSelectedConv(prev => ({ ...prev, ...updates }));
      setShowActions(false);
    } catch { /* silent */ }
  };

  const assignToMe = async () => {
    if (!selectedConv) return;
    try {
      await axios.post(`${API}/admin/chat/conversations/${selectedConv.id}/assign`);
      fetchConversations();
      setShowActions(false);
    } catch { /* silent */ }
  };

  const filteredConvs = conversations;
  const unreadTotal = conversations.reduce((sum, c) => sum + (c.unread || 0), 0);

  // ── Conversation List ─────────────────────────────────────────────
  const ConversationList = () => (
    <div className="flex flex-col h-full" data-testid="chat-conversation-list">
      {/* Search */}
      <div className="p-3 border-b border-slate-800">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
          <input
            type="text" placeholder="Search conversations..." data-testid="chat-search"
            className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white text-xs placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            value={search} onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Status filters */}
      <div className="flex gap-1 px-3 py-2 border-b border-slate-800/50 overflow-x-auto">
        {[
          { value: "", label: "All", count: conversations.length },
          { value: "open", label: "Open" },
          { value: "pending", label: "Pending" },
          { value: "resolved", label: "Resolved" },
          { value: "escalated", label: "Escalated" },
        ].map(f => (
          <button key={f.value} onClick={() => setStatusFilter(f.value)}
            className={cn("px-2 py-1 rounded-lg text-[10px] font-medium whitespace-nowrap border transition-colors",
              statusFilter === f.value ? "bg-blue-600/20 text-blue-400 border-blue-500/30" : "text-slate-500 border-transparent hover:text-white")}
            data-testid={`filter-${f.value || 'all'}`}>
            {f.label} {f.count != null && <span className="ml-1 opacity-60">({f.count})</span>}
          </button>
        ))}
      </div>

      {/* Assignment filter */}
      <div className="flex gap-1 px-3 py-1.5 border-b border-slate-800/30">
        {[
          { value: "", label: "All" },
          { value: "me", label: "Mine" },
          { value: "unassigned", label: "Unassigned" },
        ].map(f => (
          <button key={f.value} onClick={() => setAssignedFilter(f.value)}
            className={cn("px-2 py-0.5 rounded text-[10px] font-medium transition-colors",
              assignedFilter === f.value ? "text-blue-400" : "text-slate-600 hover:text-slate-400")}
            data-testid={`assign-filter-${f.value || 'all'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Conversation rows */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32 text-slate-500 text-xs"><Loader2 size={16} className="animate-spin mr-2" />Loading...</div>
        ) : filteredConvs.length === 0 ? (
          <div className="text-center py-12 px-4" data-testid="chat-empty-list">
            <Inbox size={32} className="mx-auto text-slate-700 mb-3" />
            <p className="text-sm text-slate-500">{search ? "No conversations match your search." : "No conversations yet."}</p>
            <p className="text-[10px] text-slate-600 mt-1">Users will appear here when they send messages.</p>
          </div>
        ) : (
          filteredConvs.map(conv => (
            <button
              key={conv.id || conv.user_id}
              onClick={() => selectConversation(conv)}
              data-testid={`chat-conv-${conv.user_id}`}
              className={cn("w-full text-left px-4 py-3 border-b border-slate-800/30 hover:bg-slate-800/40 transition-colors",
                (selectedConv?.user_id === conv.user_id) && "bg-slate-800/60")}
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-[11px] font-bold text-white shrink-0">
                  {(conv.user_name || conv.user_email || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-white text-sm truncate">{conv.user_name || conv.user_email || "User"}</span>
                    <span className="text-[10px] text-slate-600 whitespace-nowrap">{timeAgo(conv.last_time)}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 truncate mt-0.5">{conv.last_message || "No messages"}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <StatusBadge status={conv.status} />
                    <PriorityDot priority={conv.priority} />
                    {conv.assigned_to_name && <span className="text-[9px] text-slate-600 truncate">→ {conv.assigned_to_name}</span>}
                  </div>
                </div>
                {conv.unread > 0 && (
                  <span className="bg-blue-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">{conv.unread}</span>
                )}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );

  // ── Message Area ──────────────────────────────────────────────────
  const MessageArea = () => {
    if (!selectedConv) {
      return (
        <div className="flex items-center justify-center h-full text-slate-500" data-testid="chat-empty-state">
          <div className="text-center">
            <MessageCircle size={40} className="mx-auto text-slate-700 mb-3" />
            <p className="text-sm">Select a conversation to view its messages.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full" data-testid="chat-message-area">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800 bg-slate-900/50">
          <button onClick={handleBack} className="md:hidden text-slate-400 hover:text-white" data-testid="chat-back"><ArrowLeft size={18} /></button>
          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-[11px] font-bold text-white shrink-0">
            {(selectedConv.user_name || selectedConv.user_email || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-white text-sm truncate">{selectedConv.user_name || selectedConv.user_email || "User"}</div>
            <div className="text-[10px] text-slate-500">{selectedConv.user_email}</div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={selectedConv.status} />
            <div className="relative">
              <button onClick={() => setShowActions(!showActions)} className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors" data-testid="chat-actions-btn">
                <MoreVertical size={16} />
              </button>
              {showActions && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-50 py-1" data-testid="chat-actions-menu">
                  <button onClick={assignToMe} className="w-full text-left px-3 py-2 text-xs text-slate-300 hover:bg-slate-700 flex items-center gap-2" data-testid="action-assign-me">
                    <UserPlus size={13} /> Assign to Me
                  </button>
                  <div className="border-t border-slate-700 my-1" />
                  <div className="px-3 py-1 text-[10px] text-slate-500 font-medium">Status</div>
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <button key={key} onClick={() => updateConversation({ status: key })}
                      className={cn("w-full text-left px-3 py-1.5 text-xs hover:bg-slate-700 flex items-center gap-2",
                        selectedConv.status === key ? "text-white" : "text-slate-400")}
                      data-testid={`action-status-${key}`}>
                      <CheckCircle size={12} /> {cfg.label}
                    </button>
                  ))}
                  <div className="border-t border-slate-700 my-1" />
                  <div className="px-3 py-1 text-[10px] text-slate-500 font-medium">Priority</div>
                  {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                    <button key={key} onClick={() => updateConversation({ priority: key })}
                      className={cn("w-full text-left px-3 py-1.5 text-xs hover:bg-slate-700 flex items-center gap-2",
                        selectedConv.priority === key ? "text-white" : "text-slate-400")}
                      data-testid={`action-priority-${key}`}>
                      <PriorityDot priority={key} /> {cfg.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {msgsLoading ? (
            <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-slate-600" /></div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-500">No messages yet.</div>
          ) : (
            messages.map(m => {
              const isNote = m.type === "internal_note";
              const isAdmin = m.sender === "admin";
              return (
                <div key={m.id} className={cn("flex", isAdmin ? "justify-end" : "justify-start")} data-testid={`msg-${m.id}`}>
                  <div className={cn("max-w-[75%] px-3 py-2 rounded-xl text-sm relative",
                    isNote ? "bg-amber-500/10 border border-amber-500/20 border-dashed text-amber-200" :
                    isAdmin ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-200")}>
                    {isNote && (
                      <div className="flex items-center gap-1 mb-1 text-[10px] text-amber-400 font-medium">
                        <FileText size={10} /> Internal Note
                      </div>
                    )}
                    <div className="whitespace-pre-wrap break-words">{m.message}</div>
                    <div className={cn("text-[10px] mt-1 flex items-center gap-1",
                      isNote ? "text-amber-400/60" : isAdmin ? "text-blue-200/60" : "text-slate-500")}>
                      {m.sender_name && <span>{m.sender_name} · </span>}
                      {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {/* Composer */}
        <div className="p-3 border-t border-slate-800 bg-slate-900/50">
          {/* Type toggle */}
          <div className="flex gap-2 mb-2">
            <button onClick={() => setMsgType("message")}
              className={cn("text-[10px] font-medium px-2 py-0.5 rounded-lg border transition-colors",
                msgType === "message" ? "bg-blue-600/20 text-blue-400 border-blue-500/30" : "text-slate-500 border-slate-700 hover:text-white")}
              data-testid="type-message">
              <Send size={10} className="inline mr-1" /> Reply
            </button>
            <button onClick={() => setMsgType("internal_note")}
              className={cn("text-[10px] font-medium px-2 py-0.5 rounded-lg border transition-colors",
                msgType === "internal_note" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" : "text-slate-500 border-slate-700 hover:text-white")}
              data-testid="type-note">
              <FileText size={10} className="inline mr-1" /> Internal Note
            </button>
          </div>
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              data-testid="chat-input"
              value={newMsg}
              onChange={e => setNewMsg(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder={msgType === "internal_note" ? "Write an internal note (not visible to user)..." : "Type a reply..."}
              className={cn("flex-1 px-4 py-2 border rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 resize-none min-h-[40px] max-h-[100px]",
                msgType === "internal_note" ? "bg-amber-500/5 border-amber-500/20 focus:ring-amber-500/20" : "bg-slate-950 border-slate-800 focus:ring-blue-500/20")}
              rows={1}
            />
            <button onClick={sendMessage} disabled={!newMsg.trim() || sending} data-testid="chat-send"
              className={cn("px-4 py-2 rounded-xl font-medium text-sm transition-colors disabled:opacity-40",
                msgType === "internal_note" ? "bg-amber-600 text-white hover:bg-amber-500" : "bg-blue-600 text-white hover:bg-blue-500")}>
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-[calc(100vh-8rem)]" data-testid="admin-chat">
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle size={24} className="text-blue-400" />
        <h2 className="text-2xl font-bold text-white">Chat</h2>
        {unreadTotal > 0 && (
          <span className="bg-blue-600 text-white text-[10px] font-bold rounded-full px-2 py-0.5">{unreadTotal} unread</span>
        )}
      </div>
      <div className="flex h-[calc(100%-3rem)] rounded-2xl border border-slate-800 overflow-hidden">
        {/* Conversation list */}
        <div className={cn("flex-col w-full md:w-80 lg:w-[320px] border-r border-slate-800 bg-slate-900/50",
          selectedConv ? "hidden md:flex" : "flex")}>
          <ConversationList />
        </div>

        {/* Message area */}
        <div className={cn("flex-col flex-1 bg-slate-950/50",
          selectedConv ? "flex" : "hidden md:flex")}>
          <MessageArea />
        </div>
      </div>
    </div>
  );
}
